import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, FormArray, FormControl } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class FilesService {
    constructor(
        private http: HttpClient,
    ) {}

    getToken() {
        const jwtToken = (document.cookie.split(';').find(x => x.includes('jwtToken')) || '=').split('=')[1]

        return jwtToken || ''
    }

    uploadCustomerSign(file: File, customerId: number) {
        const formData = new FormData()
        formData.append(file.name, file, file.name)

        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/sign/customer/${customerId}`, formData, { headers: headers })
    }

    uploadFollowupSign(file: File, userId: number) {
        const formData = new FormData()
        formData.append(file.name, file, file.name)

        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/sign/followup/${userId}`, formData, { headers: headers })
    }
    
    uploadUserSign(file: File, userId: number) {
        const formData = new FormData()
        formData.append(file.name, file, file.name)

        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/sign/user/${userId}`, formData, { headers: headers })
    }

    uploadProfile(file: File, userId: number) {
        const formData = new FormData()
        formData.append(file.name, file, file.name)

        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/profile/${userId}`, formData, { headers: headers })
    }

    uploadContracts(payload: FormData, recordId: number) {
        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/contracts/${recordId}`, payload, { headers: headers })
    }

    uploadFormFiles(key: string, files: File[], recordId: number, formId: number) {
        const payload = new FormData()

        for (let file of files) {
            if (file instanceof File) {
                payload.append(key, file, file.name)
            }
        }

        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.post<any>(`${environment.apiUrl}/files/upload/${recordId}/${formId}`, payload, { headers: headers })
    }

    // ============================
    //  RECURSIVE FORM PROCESSOR
    // ============================
    async changeFilesIntoUrls(formGroup: FormGroup, formId: number, recordId: number) {
        for (let key of Object.keys(formGroup.controls)) {
            const ctrl = formGroup.get(key);

            // Process nested arrays
            if (ctrl instanceof FormArray) {
                for (let group of ctrl.controls) {
                    await this.changeFilesIntoUrls(group as FormGroup, formId, recordId);
                }
            }

            // Process normal fields
            if (ctrl instanceof FormControl && Array.isArray(ctrl.value)) {
                const files = ctrl.value;
                const hasFiles = files.some(f => f instanceof File);

                if (hasFiles) {
                    await this.uploadFilesSafely(key, files, formId, recordId, ctrl);
                }
            }
        }

        return formGroup;
    }

    // ============================
    //  UPLOAD FILES (NO PROGRESS)
    // ============================
    async uploadFilesSafely(
        key: string,
        fileList: any[],
        formId: number,
        recordId: number,
        ctrl: AbstractControl
    ) {
        try {
            // Upload files
            const res = await this.uploadFormFiles(key, fileList, recordId, formId).toPromise()

            // Values already in field (only strings)
            const existing = fileList.filter(f => typeof f === 'string')

            // Backend URLs response
            const uploaded = res.urls || res.body?.urls || []

            // Build final list cleanly
            const finalFiles = [...existing, ...uploaded].filter(v => typeof v === 'string' || (typeof v === 'object' && Object.keys(v).length))

            // Replace control value
            ctrl.setValue(finalFiles)
        } catch (error) {
            console.error(`Error uploading files for key "${key}":`, error);
        }
    }

    deleteFile(url) {
        const headers = {
            'Authorization': this.getToken()
        }

        return this.http.delete<any>(url, { headers: headers })
    }

    downloadFile(url: string, filename: string) {
        this.http.get(url, { responseType: 'blob' as 'blob', observe: 'response' })
            .subscribe(response => {
                const blob = response.body!

                // Get file name from Content-Disposition
                const contentDisposition = response.headers.get('Content-Disposition')

                filename = filename // fallback

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match) {
                        filename = match[1];
                    }
                }
                // Save blob as object url
                const fileURL = URL.createObjectURL(blob)

                // Download QR image
                const link = document.createElement('a')
                link.href = fileURL
                link.download = filename;
                link.click()

                URL.revokeObjectURL(fileURL)
            });
    }
}